
import React from 'react';
import { motion } from 'framer-motion';
import { Heart, MessageCircle, User, X, Bell } from 'lucide-react';
import { Notification } from '../types';
import { useNavigate } from 'react-router-dom';

interface NotificationDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    notifications: Notification[];
}

const NotificationDrawer: React.FC<NotificationDrawerProps> = ({ isOpen, onClose, notifications }) => {
    const navigate = useNavigate();

    const getNotificationIcon = (type: string) => {
        switch(type) {
            case 'like': return <Heart size={14} className="text-white fill-current" />;
            case 'comment': return <MessageCircle size={14} className="text-white" />;
            default: return <User size={14} className="text-white" />;
        }
    };
  
    const getNotificationColor = (type: string) => {
        switch(type) {
            case 'like': return 'bg-rose-500';
            case 'comment': return 'bg-indigo-500';
            default: return 'bg-slate-500';
        }
    };

    const handleNotificationClick = (note: Notification) => {
        onClose();
        if (note.type === 'comment' || note.type === 'like') {
            if (['poem', 'article', 'essay'].includes(note.resource_type as string)) {
                navigate(`/post/${note.resource_id}?openComments=true`);
            } else if (note.resource_type === 'book') {
                navigate(`/book/${note.resource_id}?tab=reviews`);
            } else if (note.resource_type === 'chapter') {
                // Should link to Reader with specific chapter and open comments
                // Assuming note.book_id is available in context or aux data
                // If mocked, we use generic ID. In real app, notification needs book_id.
                const bookId = note.book_id || 'b1'; // Fallback for mock
                const chapterIdx = note.chapter_index || 1;
                navigate(`/read/${bookId}?chapter=${chapterIdx}&openComments=true`, { state: { fromApp: true } });
            }
        } else if (note.type === 'follow') {
            navigate(`/author/${note.actor_id}`);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]"
            />
            <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed top-0 right-0 bottom-0 w-80 bg-white z-[70] shadow-2xl flex flex-col"
            >
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                <h2 className="text-lg font-bold text-slate-900">Notifications</h2>
                <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors">
                    <X size={20} className="text-slate-500" />
                </button>
            </div>
            <div className="flex-1 overflow-y-auto">
                {notifications.length === 0 ? (
                    <div className="p-8 text-center text-slate-400">
                        <Bell size={32} className="mx-auto mb-3 opacity-30" />
                        <p className="text-sm">No new notifications.</p>
                    </div>
                ) : (
                    <div>
                        {notifications.map(note => (
                            <div 
                                key={note.id} 
                                onClick={() => handleNotificationClick(note)}
                                className={`p-4 border-b border-slate-50 flex gap-3 cursor-pointer hover:bg-slate-50 transition-colors ${!note.is_read ? 'bg-indigo-50/30' : 'bg-white'}`}
                            >
                                <div className="relative flex-shrink-0">
                                    <img src={note.actor_avatar} className="w-10 h-10 rounded-full object-cover border border-slate-100" />
                                    <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full border-2 border-white flex items-center justify-center ${getNotificationColor(note.type)}`}>
                                        {getNotificationIcon(note.type)}
                                    </div>
                                </div>
                                <div>
                                    <p className="text-sm text-slate-800">
                                        <span className="font-bold">{note.actor_name}</span>
                                        {note.type === 'like' && ' liked your work.'}
                                        {note.type === 'comment' && ' commented: '}
                                        {note.type === 'follow' && ' started following you.'}
                                    </p>
                                    {note.preview_text && (
                                        <p className="text-xs text-slate-500 mt-1 italic line-clamp-2">"{note.preview_text}"</p>
                                    )}
                                    <p className="text-[10px] text-slate-400 mt-1">{new Date(note.created_at).toLocaleDateString()}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            </motion.div>
        </>
    );
};

export default NotificationDrawer;
